import React, { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container } from "react-grid-system";
import "../components/CSMShopfloorHeader/CSMShopfloorHeader.css";
import LOGO_SRC from "../Icons/Logo.svg";
import Footer from "../components/CSMFooter/CSMFooter";
import Spinner from "../components/Spinner/Spinner";
import { getDefaultOrganization } from "../components/CSMShopfloorHeader/CSMShopfloorHeader";
import { LayoutContainer, StyledLayout } from "../layouts/Default/styles";
import { useStoreState } from "../store";
import { canonicalOrgCode, supportOrgFor } from "../config/supportOrgs";
import { isCorpWhoto } from "../utility/supportTenant";
import { useAccess } from "./AccessProvider";
import { useOrganizationSwitch } from "./useOrganizationSwitch";
import {
  Actions,
  Body,
  BrandLogo,
  BrandName,
  Centre,
  Detail,
  Heading,
  Panel,
  PrimaryButton,
  SecondaryButton,
} from "./AccessGate.styles";

/** The org switcher. The one route a refused switch lets through: see `AccessGate`. */
export const ORG_SWITCHER_PATH = "/choose-organization";

/**
 * The app's frame with nothing in it that depends on access. `DefaultLayout` cannot be used
 * here: it draws the manager ribbon and the board's toggle by path. The footer stays, so
 * Submit Feedback can be reached when access cannot be read.
 */
const AccessFrame = ({ children }: { children: React.ReactNode }) => (
  <StyledLayout>
    <Container fluid id="layout-container">
      <div className="csm-header">
        <div className="logo-parent">
          <BrandLogo className="logo">
            <img className="logo-child" src={LOGO_SRC} alt="Swagelok" />
          </BrandLogo>
          <div className="frame-parent">
            <BrandName>CSM</BrandName>
          </div>
        </div>
      </div>
      <LayoutContainer>
        <div id="page-body" className="content-scrollable">
          <Centre>{children}</Centre>
        </div>
      </LayoutContainer>
    </Container>
    <Footer />
  </StyledLayout>
);

/** Focus the heading once, on entry, so a screen reader starts where the screen does. */
function useFocusOnEntry() {
  const ref = useRef<HTMLHeadingElement>(null);
  // A layout effect: focus is moved before the browser paints, and before any later effect
  // can move it somewhere else.
  useLayoutEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

/** Sends the user to /logout, which clears the auth cache and the store and then signs out. */
function useSignOut() {
  const navigate = useNavigate();
  return () => navigate("/logout");
}

const AccessWaiting = () => (
  <AccessFrame>
    <Panel data-state="loading" aria-labelledby="access-gate-heading">
      <Spinner inline size={32} label="Checking your access" />
      <Heading id="access-gate-heading">Checking your access</Heading>
      <Body>CSM is reading what your account can do here. This usually takes a moment.</Body>
    </Panel>
  </AccessFrame>
);

const AccessCheckFailed = ({ retry }: { retry: () => void }) => {
  const heading = useFocusOnEntry();
  const signOut = useSignOut();
  // A second press before the screen is replaced does nothing. The guard is a ref, because two
  // presses can arrive before React renders. `retry` also ignores any call that is not from
  // the failed state.
  const pressed = useRef(false);
  const [pending, setPending] = useState(false);

  const onRetry = () => {
    if (pressed.current) return;
    pressed.current = true;
    setPending(true);
    retry();
  };

  return (
    <AccessFrame>
      <Panel data-state="failed" aria-labelledby="access-gate-heading">
        <Heading id="access-gate-heading" tabIndex={-1} ref={heading}>
          We couldn&apos;t verify your access.
        </Heading>
        <Body>
          CSM could not read what your account can do here, so nothing is shown yet. This is
          usually temporary and is not a problem with your account.
        </Body>
        <Actions>
          <PrimaryButton type="button" onClick={onRetry} aria-disabled={pending || undefined}>
            {pending ? "Retrying…" : "Retry"}
          </PrimaryButton>
          <SecondaryButton type="button" onClick={signOut}>
            Sign out
          </SecondaryButton>
        </Actions>
      </Panel>
    </AccessFrame>
  );
};

/**
 * The account cannot be used (`401 ACCOUNT_UNAVAILABLE`). Shown by the gate for `GET api/me`,
 * and by PrivateRoute when licensing itself is answered so on a page load with no auth cache.
 */
export const AccountUnavailable = () => {
  const heading = useFocusOnEntry();
  const signOut = useSignOut();

  return (
    <AccessFrame>
      <Panel data-state="unusable" aria-labelledby="access-gate-heading">
        <Heading id="access-gate-heading" tabIndex={-1} ref={heading}>
          This account can&apos;t be used in CSM.
        </Heading>
        <Body>
          You are signed in, but CSM cannot use this account. Ask a manager at your site or IT to
          check it, or sign out and sign in with another account.
        </Body>
        <Actions>
          <PrimaryButton type="button" onClick={signOut}>
            Sign out
          </PrimaryButton>
        </Actions>
      </Panel>
    </AccessFrame>
  );
};

const OrganizationSwitchFailed = () => {
  const heading = useFocusOnEntry();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { switchTo } = useOrganizationSwitch();
  const user = useStoreState((s: any) => s.user);
  // A ref, as for Retry: two presses can arrive before React renders.
  const pressed = useRef(false);
  const [pending, setPending] = useState(false);

  // The header label still names the organisation that was asked for.
  const refused = canonicalOrgCode(getDefaultOrganization()) || null;
  const own = supportOrgFor(user?.info?.whoto)?.code ?? null;
  // A support-bootstrap user's own site is itself an override: when that is what was refused,
  // going "back" would ask the same thing again, so only signing out is offered.
  const canReturn = isCorpWhoto(own) && own !== null && own !== refused;

  const onReturn = async () => {
    if (pressed.current || !own) return;
    pressed.current = true;
    setPending(true);
    // The provider reads GET api/me for the user's own site, and this screen gives way to the
    // wait and then to the screen the user is on.
    if (!(await switchTo(own))) {
      pressed.current = false;
      setPending(false);
    }
  };

  return (
    <AccessFrame>
      <Panel data-state="switch-failed" aria-labelledby="access-gate-heading">
        <Heading id="access-gate-heading" tabIndex={-1} ref={heading}>
          The switch to {refused ?? "that organization"} didn&apos;t go through.
        </Heading>
        <Body>
          CSM could not connect you to {refused ?? "it"} as its support user, so its screens are
          not shown.
        </Body>
        {canReturn ? (
          <Detail>You can go back to {own}, choose another organization, or sign out.</Detail>
        ) : null}
        <Actions>
          {canReturn ? (
            <PrimaryButton type="button" onClick={onReturn} aria-disabled={pending || undefined}>
              {pending ? "Returning…" : `Return to ${own}`}
            </PrimaryButton>
          ) : null}
          {/* The picker's own switch resolves only for a CORP or CORP2 user
              (`resolveSupportTarget`), so the button is offered on the same test. The answer
              in use cannot say: every capability is false after a refused switch. */}
          {isCorpWhoto(user?.info?.whoto) ? (
            <SecondaryButton type="button" onClick={() => navigate(ORG_SWITCHER_PATH)}>
              Choose another organization
            </SecondaryButton>
          ) : null}
          <SecondaryButton type="button" onClick={signOut}>
            Sign out
          </SecondaryButton>
        </Actions>
      </Panel>
    </AccessFrame>
  );
};

/**
 * Stands between licensing and the routes (PrivateRoute renders it after every licensing
 * state and before the Analytics-only decision). Until the provider has an answer it can use,
 * none of the route's children is rendered, so no screen, tab or redirect is decided on
 * access that is not known.
 *
 * - `loading`: a neutral wait in a frame with no controls.
 * - `failed`: "We couldn't verify your access." with Retry, which calls `retry()`.
 * - `unusable`: the account cannot be used, with Sign out. Nothing is asked again.
 * - a refused organisation switch: the target's screens are not shown; the way back is offered.
 *   The org switcher alone is let through, because choosing again is the way out, and it shows
 *   the refusal itself.
 * - `ready` and `refreshing`: the children, the same element, so a refresh that keeps the scope
 *   keeps them mounted and keeps what was typed in them.
 */
export const AccessGate = ({ children }: { children: React.ReactElement }) => {
  const access = useAccess();
  const location = useLocation();

  if (access.status === "loading") return <AccessWaiting />;
  if (access.status === "failed") return <AccessCheckFailed retry={access.retry} />;
  if (access.status === "unusable") return <AccountUnavailable />;
  if (access.switchFailed && location.pathname.toLowerCase() !== ORG_SWITCHER_PATH) {
    return <OrganizationSwitchFailed />;
  }
  return children;
};
